import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable,
    inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    Chain,
    ChainOperation,
    ChainSearchFilterOptions,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetRequest,
    RequestUtil,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";

/**
 * API Client Service for Chain entities.
 * Implements IFullAccessInternalApiClientService providing full CRUD access including
 * bulk operations for the Chain model via the Internal API v2, plus the Activate and Deactivate custom operations.
 *
 * **Service Type:** API Client Service (IFullAccessInternalApiClientService)
 *
 * **Provided:** Root-level singleton
 */
@Injectable({
    providedIn: "root"
})
export class ChainApiClientService implements IFullAccessInternalApiClientService<Chain> {

    readonly basePath = "chain";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Search for chains with given search filter options.
     *
     * @param filterOptions - Filters to apply to the search
     * @param page - Zero-based page index
     * @param pageSize - Number of results per page
     * @param sort - Sort criteria
     * @returns Observable that emits the search response
     * @see InternalApiRequestService.search$
     */
    search$(
        filterOptions: ChainSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Chain>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Chain.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(searchRequest, this.basePath);
    }

    /**
     * Get all chains matching the given search filter options.
     * Appends all paginated results to response.
     *
     * @param filterOptions - Filters to apply to the search
     * @param sort - Sort criteria
     * @returns Observable that emits every matching chain
     * @see InternalApiRequestService.getAll$
     */
    getAll$(filterOptions: ChainSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Chain>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Chain.modelName,
            0,
            100,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.getAll$(searchRequest, this.basePath);
    }

    /**
     * Get a chain by ID.
     *
     * @param id - ID of the chain to get
     * @returns Observable that emits the chain
     * @see InternalApiRequestService.get$
     */
    get$(id: string): Observable<Chain> {

        const getRequest: GetRequest<Chain> = RequestUtil.createGetRequest(
            id, Chain.modelName, ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(map((response) => response.payload as Chain));
    }

    /**
     * Activates a chain via the Activate custom operation.
     * Never implement this as an `active: true` update: the server runs the FlyerVersion listener off the custom operation.
     *
     * @param chain - Chain to activate; its id and esVersion are sent for optimistic locking
     * @returns Observable that emits the updated chain
     * @throws {InvalidParametersException} When the chain or its id is not provided
     * @see InternalApiRequestService.customOp$
     */
    activate$(chain: Chain): Observable<Chain> {
        return this._chainCustomOp$(chain, ChainOperation.Activate);
    }

    /**
     * Deactivates a chain via the Deactivate custom operation.
     * Never implement this as `delete$` or an `active: false` update: the server hangs a FlyerVersion listener off
     * the custom operation, which both of those would skip.
     *
     * @param chain - Chain to deactivate; its id and esVersion are sent for optimistic locking
     * @returns Observable that emits the updated chain
     * @throws {InvalidParametersException} When the chain or its id is not provided
     * @see InternalApiRequestService.customOp$
     */
    deactivate$(chain: Chain): Observable<Chain> {
        return this._chainCustomOp$(chain, ChainOperation.Deactivate);
    }

    /**
     * Bulk get chains by their IDs.
     *
     * @param ids - IDs of the chains to get
     * @returns Observable that emits the bulk response
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Chain>(id, Chain.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Create a chain.
     *
     * @param fields - Chain to create
     * @returns Observable that emits the created chain
     * @see InternalApiRequestService.create$
     */
    create$(fields: Chain): Observable<Chain> {

        const createRequest = RequestUtil.createCreateRequest<Chain, void>(
            ModelAction.ClientCreate,
            fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Bulk create chains.
     *
     * @param items - Create requests to send in the bulk request
     * @returns Observable that emits the bulk response
     */
    bulkCreate$(items: Array<CreateRequest<Chain, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update a chain by ID and version.
     *
     * @param id - ID of the chain to update
     * @param version - Current version of the chain, for optimistic locking
     * @param fields - Fields to update
     * @param removeFields - Names of fields to remove
     * @returns Observable that emits the updated chain
     * @see InternalApiRequestService.update$
     */
    update$(id: string, version: number, fields: Chain, removeFields?: Array<string>): Observable<Chain> {

        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Chain> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Update a chain with changes computed from original and updated models.
     *
     * @param original - Chain as originally loaded
     * @param updated - Chain with the changes applied
     * @returns Observable that emits the updated chain
     */
    updateWithChanges$(original: Chain, updated: Chain): Observable<Chain> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Bulk update chains.
     *
     * @param requests - Update requests to send in the bulk request
     * @returns Observable that emits the bulk response
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a chain by ID and version.
     *
     * @param id - ID of the chain to delete
     * @param version - Current version of the chain, for optimistic locking
     * @returns Observable that emits the delete response
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Chain>(
            id, Chain.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Bulk delete chains.
     *
     * @param items - Delete requests to send in the bulk request
     * @returns Observable that emits the bulk response
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    private _chainCustomOp$(chain: Chain, operation: ChainOperation): Observable<Chain> {

        if (!chain?.id) {
            throw new InvalidParametersException("id for the chain is required");
        }

        const request = RequestUtil.createCustomOpRequest<void>(
            chain.id, Chain.modelName, operation, undefined, chain.esVersion
        );

        return this._internalApiRequestService.customOp$<Chain>(request, this.basePath).pipe(
            map((response) => response.payload)
        );
    }
}
